import type { LocationT, MakeT, ServiceT } from "./financial-service-links";
import { FINANCIAL_SERVICE_LINKS } from "./financial-service-links";

export type FunnelStepIdT = "intro" | "location" | "make" | "service";

export type FunnelItemT = {
  id: string;
  title: string;
  description: string;
  badgeLabel: string;
  type: "info" | "location" | "make" | "service";
  href?: string;
  disabled?: boolean;
};

export type FunnelInfoItemT = FunnelItemT & {
  type: "info";
};

export type LocationItemT = FunnelItemT & {
  type: "location";
  location: LocationT;
};

export type MakeItemT = FunnelItemT & {
  type: "make";
  make: MakeT;
};

export type ServiceItemT = FunnelItemT & {
  type: "service";
  service: ServiceT;
};

export type FunnelStateSnapshotT = {
  selectedLocation: LocationT | null;
  selectedMake: MakeT | null;
  selectedService: ServiceT | null;
};

export type FunnelActionsT = {
  selectLocation: (location: LocationT) => void;
  selectMake: (make: MakeT) => void;
  selectService: (service: ServiceT) => void;
};

export type FunnelStepContextT = {
  state: FunnelStateSnapshotT;
  actions: FunnelActionsT;
};

export type FunnelCardViewModelT = {
  id: string;
  title: string;
  description?: string;
  badgeLabel: string;
  href?: string;
  target?: string;
  rel?: string;
  disabled?: boolean;
  isClickable?: boolean;
  onClick?: () => void;
};

export type FunnelStepConfigT = {
  id: FunnelStepIdT;
  heading: string;
  subheading: string;
  cards: (context: FunnelStepContextT) => FunnelCardViewModelT[];
};

export const DEFAULT_FUNNEL_STEP: FunnelStepIdT = "intro";

const FUNNEL_INTRO_CARDS: FunnelInfoItemT[] = [
  {
    id: "step-1",
    type: "info",
    title: "Standort wählen",
    description: "Wählen Sie Ihren Wiest Group Standort aus",
    badgeLabel: "1",
  },
  {
    id: "step-2",
    type: "info",
    title: "Marke wählen",
    description: "Wählen Sie Ihre Fahrzeugmarke aus",
    badgeLabel: "2",
  },
  {
    id: "step-3",
    type: "info",
    title: "Service wählen",
    description: "Wählen Sie den gewünschten Service aus",
    badgeLabel: "3",
  },
  {
    id: "step-4",
    type: "info",
    title: "Preis anfragen",
    description: "Fragen Sie Ihren individuellen Preis an",
    badgeLabel: "4",
  },
];

/**
 * Locations data
 */
export const FUNNEL_LOCATIONS: LocationItemT[] = [
  {
    id: "location-darmstadt",
    type: "location",
    title: "Darmstadt",
    description: "Ihr Wiest Group Standort in Darmstadt",
    badgeLabel: "D",
    location: {
      title: "Darmstadt",
      description: "Ihr Wiest Group Standort in Darmstadt",
    },
  },
  {
    id: "location-bensheim",
    type: "location",
    title: "Bensheim",
    description: "Ihr Wiest Group Standort in Bensheim",
    badgeLabel: "B",
    location: {
      title: "Bensheim",
      description: "Ihr Wiest Group Standort in Bensheim",
    },
  },
];

/**
 * Makes available by location
 */
export const MAKES_BY_LOCATION: Record<LocationT["title"], MakeT[]> = {
  Darmstadt: ["Volkswagen", "Audi", "VW Nutzfahrzeuge", "Skoda"],
  Bensheim: ["Volkswagen", "Audi"],
};

/**
 * Makes data with descriptions
 */
const MAKE_DESCRIPTIONS: Record<MakeT, string> = {
  Volkswagen: "Marke auswählen",
  Audi: "Marke auswählen",
  "VW Nutzfahrzeuge": "Marke auswählen",
  Skoda: "Marke auswählen",
};

/**
 * Services data with descriptions
 */
const SERVICE_DESCRIPTIONS: Record<ServiceT, string> = {
  Garantieversicherung: "Direkt zur Anfrage",
  "Wartung & Inspektion": "Direkt zur Anfrage",
  Kaufpreisschutz: "Direkt zur Anfrage",
  "Kfz Versicherung": "Direkt zur Anfrage",
};

/**
 * Helper function to get badge label from title
 */
export const getBadgeLabel = (title: string): string => {
  return title.charAt(0).toUpperCase();
};

/**
 * Create make items from available makes
 */
export const createMakeItems = (makes: MakeT[]): MakeItemT[] => {
  return makes.map((make) => ({
    id: `make-${make.toLowerCase().replace(/\s+/g, "-")}`,
    type: "make",
    title: make,
    description: MAKE_DESCRIPTIONS[make],
    badgeLabel: getBadgeLabel(make),
    make,
  }));
};

/**
 * Create service items
 */
export const createServiceItems = ({
  services,
  location,
  make,
}: {
  services: ServiceT[];
  location: LocationT | null;
  make: MakeT | null;
}): ServiceItemT[] => {
  return services.map((service) => {
    const href = getServiceHref(location, make, service);

    return {
      id: `service-${service.toLowerCase().replace(/\s+/g, "-")}`,
      type: "service",
      title: service,
      description: SERVICE_DESCRIPTIONS[service],
      badgeLabel: getBadgeLabel(service),
      service,
      href: href ?? undefined,
      disabled: !href,
    };
  });
};

/**
 * Get service href based on location, make and service
 */
export const getServiceHref = (
  location: LocationT | null,
  make: MakeT | null,
  service: ServiceT
): string | null => {
  if (!location || !make) {
    return null;
  }

  return FINANCIAL_SERVICE_LINKS[location.title]?.[make]?.[service] || null;
};

/**
 * All available services
 */
export const FUNNEL_SERVICES: ServiceT[] = [
  "Garantieversicherung",
  "Wartung & Inspektion",
  "Kaufpreisschutz",
  "Kfz Versicherung",
];

const toCardViewModel = (item: FunnelItemT): FunnelCardViewModelT => ({
  id: item.id,
  title: item.title,
  description: item.description,
  badgeLabel: item.badgeLabel,
  disabled: item.disabled,
});

export const FUNNEL_FLOW: FunnelStepConfigT[] = [
  {
    id: "intro",
    heading: "So funktioniert es",
    subheading: "In 4 Schritten geht's los",
    cards: () =>
      FUNNEL_INTRO_CARDS.map((item) => ({
        ...toCardViewModel(item),
        isClickable: false,
      })),
  },
  {
    id: "location",
    heading: "Standort wählen",
    subheading: "Schritt 1",
    cards: ({ actions }) =>
      FUNNEL_LOCATIONS.map((item) => ({
        ...toCardViewModel(item),
        isClickable: true,
        onClick: () => actions.selectLocation(item.location),
      })),
  },
  {
    id: "make",
    heading: "Marke wählen",
    subheading: "Schritt 2",
    cards: ({ state, actions }) => {
      const { selectedLocation } = state;
      if (!selectedLocation) {
        return [];
      }

      const makes = MAKES_BY_LOCATION[selectedLocation.title] || [];
      return createMakeItems(makes).map((item) => ({
        ...toCardViewModel(item),
        isClickable: true,
        onClick: () => actions.selectMake(item.make),
      }));
    },
  },
  {
    id: "service",
    heading: "Service wählen",
    subheading: "Schritt 3",
    cards: ({ state, actions }) =>
      createServiceItems({
        services: FUNNEL_SERVICES,
        location: state.selectedLocation,
        make: state.selectedMake,
      }).map((item) => {
        const hasHref = Boolean(item.href);

        return {
          ...toCardViewModel(item),
          href: item.href,
          target: hasHref ? "_blank" : undefined,
          rel: hasHref ? "noopener noreferrer" : undefined,
          disabled: item.disabled,
          isClickable: hasHref,
          onClick: hasHref
            ? () => actions.selectService(item.service)
            : undefined,
        };
      }),
  },
];

export const getFunnelStepConfig = (
  stepId: FunnelStepIdT
): FunnelStepConfigT => {
  return FUNNEL_FLOW.find((step) => step.id === stepId) || FUNNEL_FLOW[0];
};

export const getFunnelStepIndex = (stepId: FunnelStepIdT): number => {
  return Math.max(
    0,
    FUNNEL_FLOW.findIndex((step) => step.id === stepId)
  );
};
